/**
 * OfferDetail Page
 * 
 * Displays detailed information for a single offer with cross-links to reward/partner.
 * Desktop: Clean sectioned layout with unified appearance
 * Mobile: Card-based layout for better scrolling
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { logger } from '@/app/utils/logger';
import { AssignmentStrategyBadge } from '@/components/common/Badge';
import { OfferContextDisplay } from '@/components/offers/OfferContextDisplay';
import { offerService } from '@/app/services/offerService';
import { auditService } from '@/app/services';
import type { OfferResponse } from '@/app/types/offers';
import './OfferDetail.scss';

export const OfferDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [offer, setOffer] = useState<OfferResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOffer = async () => {
      if (!id) {
        setError('Offer ID is missing');
        setLoading(false);
        return;
      }

      try {
        const data = await offerService.getOfferById(id);
        if (data) {
          setOffer(data);
          
          // Log record view for audit
          auditService.logRecordView('offer', id, {
            page_url: `/offers/${id}`,
          }).catch((err) => logger.warn('Failed to log record view', { error: err }));
        } else {
          setError('Offer not found');
        }
      } catch (err) {
        setError('Failed to load offer');
        logger.error('Failed to load offer', { error: err, offerId: id });
      } finally {
        setLoading(false);
      }
    };

    fetchOffer();
  }, [id]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleDelete = async () => {
    if (!id || !offer) return;
    
    if (!window.confirm('Are you sure you want to delete this offer? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      const success = await offerService.deleteOffer(id);
      if (success) {
        // Log record deletion for audit
        auditService.logRecordModification('deleted', 'offer', id, {
          reward_name: offer?.reward_name,
          user_id: offer?.user_id,
        }).catch((err) => logger.warn('Failed to log record deletion', { error: err }));
        
        navigate('/offers');
      } else {
        setError('Failed to delete offer');
      }
    } catch (err) {
      setError('Failed to delete offer');
      logger.error('Failed to delete offer', { error: err, offerId: id });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="offer-detail-page">
        <div className="loading-state">
          <div className="spinner" />
          <span>Loading offer details...</span>
        </div>
      </div>
    );
  }

  if (error || !offer) {
    return (
      <div className="offer-detail-page">
        <div className="error-state">
          <span className="error-icon">⚠️</span>
          <span className="error-message">{error || 'Offer not found'}</span>
          <button onClick={() => navigate('/offers')} className="btn-back">
            Back to Offers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="offer-detail-page">
      {/* Back Navigation */}
      <div className="back-nav">
        <button onClick={() => navigate('/offers')} className="btn-back-link">
          ← Back to Offers
        </button>
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="header-left">
          <div className="header-badges">
            <AssignmentStrategyBadge strategy={offer.assignment_strategy} />
          </div>
        </div>
        <div className="header-actions">
          <button
            className="btn-edit"
            onClick={() => navigate(`/offers/${offer.offer_id}/edit`)}
          >
            ✏️ Edit
          </button>
          <button
            className="btn-delete"
            onClick={handleDelete}
            disabled={loading}
          >
            🗑️ Delete
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="detail-container">
        {/* Basic Info Section */}
        <section className="detail-section">
          <h2 className="section-title">Offer Information</h2>
          <div className="info-grid">
            <div className="info-row">
              <span className="info-label">Offer ID</span>
              <span className="info-value mono">
                {offer.offer_id}
                <button className="copy-btn" onClick={() => copyToClipboard(offer.offer_id)}>📋</button>
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">User ID</span>
              <span className="info-value mono">
                {offer.user_id}
                <button className="copy-btn" onClick={() => copyToClipboard(offer.user_id)}>📋</button>
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">Display Priority</span>
              <span className="info-value">{offer.display_priority}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Assignment Strategy</span>
              <span className="info-value"><AssignmentStrategyBadge strategy={offer.assignment_strategy} /></span>
            </div>
          </div>
        </section>

        {/* Related Entities Section */}
        <section className="detail-section">
          <h2 className="section-title">Related Records</h2>
          <div className="info-grid two-col">
            <div className="info-group">
              <h3 className="group-title">Reward</h3>
              <div className="info-row">
                <span className="info-label">Name</span>
                <span className="info-value">
                  <Link to={`/rewards/${offer.reward_id}`} className="entity-link">
                    {offer.reward_name || 'View Reward'} →
                  </Link>
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Reward ID</span>
                <span className="info-value mono small">
                  {offer.reward_id}
                  <button className="copy-btn" onClick={() => copyToClipboard(offer.reward_id)}>📋</button>
                </span>
              </div>
            </div>
            <div className="info-group">
              <h3 className="group-title">Partner</h3>
              <div className="info-row">
                <span className="info-label">Name</span>
                <span className="info-value">
                  <Link to={`/partners/${offer.partner_product_id}`} className="entity-link">
                    {offer.partner_name || 'View Partner'} →
                  </Link>
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Partner Product ID</span>
                <span className="info-value mono">{offer.partner_product_id}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Timestamps Section */}
        <section className="detail-section">
          <h2 className="section-title">Timeline</h2>
          <div className="timeline-grid">
            <div className="timeline-item muted">
              <span className="timeline-label">Created</span>
              <span className="timeline-value">{new Date(offer.created_at).toLocaleString()}</span>
            </div>
            <div className="timeline-item muted">
              <span className="timeline-label">Updated</span>
              <span className="timeline-value">{new Date(offer.updated_at).toLocaleString()}</span>
            </div>
          </div>
        </section>

        {/* Context Section */}
        <section className="detail-section">
          <h2 className="section-title">Context Data</h2>
          <OfferContextDisplay 
            context={offer.context_json} 
            strategy={offer.assignment_strategy} 
          />
        </section>
      </div>
    </div>
  );
};

export default OfferDetail;
